import { Component, Vue, Prop } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { EnglishRules, Rules } from '@/support/ValidationRules';
import { cloneDeep } from 'lodash';
import { SupplierSizeDeviation } from '@/models/SupplierSizeDeviation';
import { Brand } from '@/models/Brand';

@Component<SupplierSizeDeviationCreateOrUpdateDialog>({})
export default class SupplierSizeDeviationCreateOrUpdateDialog extends Vue {
  @Prop({ default: undefined })
  protected originalSupplierSizeDeviation!: SupplierSizeDeviation;

  @Prop({ default: null })
  protected brand!: null | Brand;

  protected isFormValid: boolean = false;

  protected isLoading: boolean = false;

  protected supplierSizeDeviation: SupplierSizeDeviation = new SupplierSizeDeviation();

  protected rules: Rules = EnglishRules;

  protected internalSelectedBrand: null | Brand = this.brand;

  protected mounted(): void {
    this.initialize();
  }

  protected initialize(): void {
    if (this.originalSupplierSizeDeviation) {
      const supplierSizeDeviation = cloneDeep(this.originalSupplierSizeDeviation);

      this.internalSelectedBrand = supplierSizeDeviation.brand || null;

      supplierSizeDeviation.size_id = supplierSizeDeviation.size && supplierSizeDeviation.size.id
        ? supplierSizeDeviation.size.id
        : '';

      supplierSizeDeviation.supplier_size_id = supplierSizeDeviation.supplier_size && supplierSizeDeviation.supplier_size.id
        ? supplierSizeDeviation.supplier_size.id
        : '';

      this.supplierSizeDeviation = supplierSizeDeviation;
    }
  }

  protected close(): void {
    this.$emit('input', false);
  }

  protected async onSaveButtonClick(): Promise<void> {
    if (! this.canSaveSupplierSize) { return; }

    this.isLoading = true;

    const supplierSizeDeviation = this.originalSupplierSizeDeviation
      ? await this.update()
      : await this.create();

    if (supplierSizeDeviation) {
      this.originalSupplierSizeDeviation
        ? this.$emit('sizeUpdated', supplierSizeDeviation)
        : this.$emit('sizeCreated', supplierSizeDeviation);

      this.close();
    }

    this.isLoading = false;
  }

  protected async update(): Promise<null | SupplierSizeDeviation> {
    if (! this.supplierSizeDeviation) { return null; }

    const supplierSizeDeviation = await this.supplierSizeDeviation
      .update({
        ...this.supplierSizeDeviation,
        brand_id: this.internalSelectedBrandId,
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    return supplierSizeDeviation;
  }

  protected async create(): Promise<null | SupplierSizeDeviation> {
    const supplierSizeDeviation = await this.supplierSizeDeviation
      .create({
        ...this.supplierSizeDeviation,
        brand_id: this.internalSelectedBrandId,
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    return supplierSizeDeviation;
  }

  protected get canSaveSupplierSize() {
    return this.isFormValid && ! this.isLoading;
  }

  protected get internalSelectedBrandId(): string {
    return this.internalSelectedBrand
      ? this.internalSelectedBrand.id || ''
      : '';
  }

  protected get saveButtonText(): string {
    return this.originalSupplierSizeDeviation
      ? 'Edit'
      : 'Add';
  }
}
